import { ref, reactive, onBeforeMount, watch } from "@vue/composition-api";
import router from "@/router";
import store from "@/store";

import inventoryPrivilegeCpcModel from "@/models/inventory/inventory-privilege-cpc";
import usePagination from "@/views/components/pagination/usePagination";
import useFilterHeaderTable from "@/views/components/filter-header-table/useFilterHeaderTable";
import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";

export default function useInventoryStockDetail() {
  const STATUS_INSTOCK = 10;
  const STATUS_TRANSFER_IN = 11;
  const STATUS_TRANSFER_OUT = 12;
  const STATUS_OUTSTOCK = 20;
  const STATUS_OUT_STOCK_EXPORT = 21;
  const STATUS_STOCK_EXPORT_ISSUE = 22;
  const STATUS_EXPIRED = 30;

  const { inventoryExternalCodeSerialList } = inventoryPrivilegeCpcModel();

  const { pagination, onPageChange, onFilterEntity } = usePagination();

  const {
    filters,
    ordering,
    onSetValueSearch,
    onSelectFilter,
  } = useFilterHeaderTable();

  const {
    openDefaultErrorModal,
    openWarningModal,
    openSuccessModal,
  } = useValidationModal();

  const loading = reactive({
    tableData: false,
  });

  const stockRef = ref("");

  const formStatus = reactive({
    validFrom: null,
    validThrough: null,
    status: null,
  });

  const isVerified = ref(false);

  const statusOption = ref([
    { label: "In Stock", value: STATUS_INSTOCK },
    { label: "Out Stock", value: STATUS_OUTSTOCK },
    { label: "Export", value: STATUS_OUT_STOCK_EXPORT },
    { label: "Expired", value: STATUS_EXPIRED },
    { label: "Export Issue", value: STATUS_STOCK_EXPORT_ISSUE },
    { label: "Transfer In", value: STATUS_TRANSFER_IN },
    { label: "Transfer Out", value: STATUS_TRANSFER_OUT },
  ]);

  const fields = [
    { key: "no", label: "No." },
    { key: "serial", label: "Serial" },
    { key: "crm_transaction_ref", label: "Transaction Reference" },
    { key: "exp_time", label: "Expire Date" },
    { key: "status", label: "Status" },
  ];

  const stockTable = reactive({
    payload: [],
  });

  const resolveStockStatusLabel = (status) => {
    switch (status) {
      case STATUS_INSTOCK:
        return "In stock";
      case STATUS_OUTSTOCK:
        return "Out stock";
      case STATUS_OUT_STOCK_EXPORT:
        return "Export";
      case STATUS_EXPIRED:
        return "Expired";
      case STATUS_STOCK_EXPORT_ISSUE:
        return "Export Issue";
      case STATUS_TRANSFER_IN:
        return "Transfer In";
      case STATUS_TRANSFER_OUT:
        return "Transfer Out";
      default:
        return "None";
    }
  };

  const resolveStockStatusClass = (status) => {
    switch (status) {
      case STATUS_INSTOCK:
        return "in-stock";
      case STATUS_OUTSTOCK:
        return "out-stock";
      case STATUS_OUT_STOCK_EXPORT:
        return "out-export";
      case STATUS_EXPIRED:
        return "expired";
      case STATUS_STOCK_EXPORT_ISSUE:
        return "Export-Issue";
      case STATUS_TRANSFER_IN:
        return "transfer-in";
      case STATUS_TRANSFER_OUT:
        return "transfer-out";
      default:
        return "None";
    }
  };

  const fetchStockList = () => {
    loading.tableData = true;
    let payload = {
      Inventory_Id: router.currentRoute.params.invRef,
      Privilege_Id: router.currentRoute.params.ref,
      Page: pagination.currentPage,
      Limit: pagination.perPage,
      Filters: filters,
      Ordering: ordering.value,
      IsVerified: isVerified.value,
    };
    if (router.currentRoute.params.stockRef) {
      payload = {
        ...payload,
        ...{ Import_Id: router.currentRoute.params.stockRef },
      };
      stockRef.value = router.currentRoute.params.stockRef;
    }
    inventoryExternalCodeSerialList.payload = payload;
    store
      .dispatch("api/apiRequest", inventoryExternalCodeSerialList)
      .then((response) => {
        stockTable.payload = response.data.payload;
        pagination.rows = response.data.payload.length;
        pagination.totalRows = response.data.filtered_record;
      })
      .catch((error) => {
        openDefaultErrorModal(error);
      })
      .finally(() => {
        loading.tableData = false;
      });
  };

  const confirmViewData = () => {
    if (isVerified.value == false) {
      openWarningModal("ยันยืนการดูข้อมูล", `ต้องการยืนยันใช่หรือไม่ ?`, () => {
        isVerified.value = true;
        fetchStockList();
      });
    } else {
      openWarningModal(
        "ยันยืนการซ่อนข้อมูล",
        `ต้องการยืนยันใช่หรือไม่ ?`,
        () => {
          isVerified.value = false;
          fetchStockList();
        }
      );
    }
  };

  watch(
    pagination,
    () => {
      if (!loading.tableData) {
        fetchStockList();
      }
    },
    { deep: true }
  );

  watch(
    [filters, ordering],
    () => {
      pagination.currentPage = 1;

      fetchStockList();
    },
    { deep: true }
  );

  onBeforeMount(() => {
    fetchStockList();
  });

  return {
    stockRef,
    formStatus,
    fields,
    loading,
    stockTable,
    statusOption,
    resolveStockStatusLabel,
    resolveStockStatusClass,
    pagination,
    onPageChange,
    onFilterEntity,
    onSetValueSearch,
    onSelectFilter,
    confirmViewData,
    isVerified
  };
}
